import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PrimaryCountryISO } from '@/constants/countries';
import type { Industry } from '@/pages/Market/query/fetchIndustries';
import { fetchIndustries } from '@/pages/Market/query/fetchIndustries';

import { GET_INDUSTRY_QUERY_KEY } from '@/pages/Market/query/fetchIndustries/constants';

import { persistedQueryConfig } from '@/utilities/persistQueryClient/utilities';

export interface Industries {
  sector: Record<string, Industry>;
  primary: Record<string, Industry>;
  secondary: Record<string, Industry>;
  tertiary: Record<string, Industry>;
}

const useIndustriesFetch = (
  market: PrimaryCountryISO = 'us',
  filterCompanyCount = true,
  enabled = true
): { industries: Industries; isIndustriesFetched: boolean } => {
  const { data, isFetched } = useQuery({
    queryKey: [GET_INDUSTRY_QUERY_KEY, market],
    queryFn: fetchIndustries,
    enabled,
    ...persistedQueryConfig,
  });

  const industries = useMemo(() => {
    if (!data)
      return {
        sector: {},
        primary: {},
        secondary: {},
        tertiary: {},
      };
    return filterCompanyCount ? data.filtered : data.all;
  }, [data, filterCompanyCount]);

  return {
    industries,
    isIndustriesFetched: isFetched,
  };
};

export default useIndustriesFetch;
